import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import Chart from "react-apexcharts";
import { useAuth } from "../../../contexts/AuthContext";
import { isModuleEnabled } from "../../../config/modules";
import { dashboardService } from "../../../services/dashboardService";
import { appointmentService } from "../../../services/appointmentService";
import { patientService } from "../../../services/patientService";
import Breadcrumb from '../../../components/ui/Breadcrumb';
import {
  calendar,
  empty_wallet,
  morning_img_01,
  profile_add,
  scissor,
} from "../../../components/imagepath";
import { parseApiDate } from '../../../lib/format';

const Admin_Dashboard = () => {
  const { user } = useAuth();
  const [quickStats, setQuickStats] = useState(null);
  const [consultationStats, setConsultationStats] = useState(null);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [quick, consultation, appts, patients] = await Promise.all([
          dashboardService.getQuickStats(),
          dashboardService.getConsultationStats(),
          appointmentService.getAll({ page: 1, perPage: 5 }).catch(() => ({ data: [] })),
          patientService.getAll(5).catch(() => ({ data: [], total: 0 })),
        ]);
        setQuickStats(quick);
        setConsultationStats(consultation);
        setRecentAppointments(Array.isArray(appts.data) ? appts.data.slice(0, 5) : []);
        setRecentPatients(Array.isArray(patients.data) ? patients.data.slice(0, 5) : []);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const doctorChartOptions = {
    chart: { type: 'bar', height: 300, toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, horizontal: true } },
    colors: ['#2E37A4'],
    xaxis: {
      categories: consultationStats?.consultations?.by_doctor?.map(
        d => d.doctor ? `Dr. ${d.doctor.first_name} ${d.doctor.last_name}` : `Doctor #${d.doctor_id}`
      ) || [],
    },
    dataLabels: { enabled: true },
  };

  const doctorChartSeries = [{
    name: 'Consultas',
    data: consultationStats?.consultations?.by_doctor?.map(d => d.total) || [],
  }];

  const appointmentDonutOptions = {
    chart: { type: 'donut' },
    labels: ['Completadas', 'Pendientes'],
    colors: ['#00e396', '#feb019'],
    legend: { position: 'bottom' },
    responsive: [{ breakpoint: 480, options: { chart: { width: 250 } } }],
  };

  const appointmentDonutSeries = consultationStats ? [
    consultationStats.appointments.completed,
    consultationStats.appointments.pending,
  ] : [0, 0];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return (parseApiDate(dateStr) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      {/* Breadcrumb */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Panel Principal' }]} />
              </div>
            </div>
          </div>

          {/* Greeting */}
          <div className="good-morning-blk">
            <div className="row">
              <div className="col-md-6">
                <div className="morning-user">
                  <h2>{getGreeting()}, <span>{user?.name || 'Usuario'}</span></h2>
                  <p>Resumen del sistema eCliniq</p>
                </div>
              </div>
              <div className="col-md-6 position-blk">
                <div className="morning-img">
                  <img src={morning_img_01} alt="" />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Widgets */}
          <div className="row">
            <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3">
              <div className="dash-widget">
                <div className="dash-boxs comman-flex-center">
                  <img src={calendar} alt="" />
                </div>
                <div className="dash-content dash-count flex-grow-1">
                  <h4>Citas Hoy</h4>
                  <h2><CountUp delay={0.4} end={quickStats?.today_appointments || 0} duration={0.6} /></h2>
                  <p><span className="text-muted">Programadas para hoy</span></p>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3">
              <div className="dash-widget">
                <div className="dash-boxs comman-flex-center">
                  <img src={profile_add} alt="" />
                </div>
                <div className="dash-content dash-count">
                  <h4>Total Pacientes</h4>
                  <h2><CountUp delay={0.4} end={quickStats?.total_patients || 0} duration={0.6} /></h2>
                  <p><span className="text-muted">Registrados en el sistema</span></p>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3">
              <div className="dash-widget">
                <div className="dash-boxs comman-flex-center">
                  <img src={scissor} alt="" />
                </div>
                <div className="dash-content dash-count">
                  <h4>Consultas Hoy</h4>
                  <h2><CountUp delay={0.4} end={quickStats?.today_consultations || 0} duration={0.6} /></h2>
                  <p><span className="text-muted">Realizadas hoy</span></p>
                </div>
              </div>
            </div>
            {isModuleEnabled('billing') && (
            <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3">
              <div className="dash-widget">
                <div className="dash-boxs comman-flex-center">
                  <img src={empty_wallet} alt="" />
                </div>
                <div className="dash-content dash-count">
                  <h4>Pre-Autorizaciones</h4>
                  <h2><CountUp delay={0.4} end={quickStats?.pending_authorizations || 0} duration={0.6} /></h2>
                  <p><span className="text-muted">Pendientes de aprobación</span></p>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Charts Row */}
          <div className="row">
            <div className="col-12 col-md-12 col-lg-6 col-xl-8">
              <div className="card">
                <div className="card-body">
                  <div className="chart-title">
                    <h4>Consultas por Doctor</h4>
                    <p className="text-muted mb-0">
                      Período: {consultationStats?.period?.date_from || '-'} a {consultationStats?.period?.date_to || '-'}
                    </p>
                  </div>
                  {consultationStats?.consultations?.by_doctor?.length > 0 ? (
                    <Chart options={doctorChartOptions} series={doctorChartSeries} type="bar" height={300} />
                  ) : (
                    <div className="text-center text-muted py-5">
                      <i className="fa fa-bar-chart fa-3x mb-3 d-block" style={{ opacity: 0.3 }}></i>
                      No hay consultas registradas en este período
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-md-12 col-lg-6 col-xl-4 d-flex">
              <div className="card w-100">
                <div className="card-body">
                  <div className="chart-title">
                    <h4>Estado de Citas</h4>
                    <p className="text-muted mb-0">Este mes</p>
                  </div>
                  {(consultationStats?.appointments?.total || 0) > 0 ? (
                    <Chart options={appointmentDonutOptions} series={appointmentDonutSeries} type="donut" height={280} />
                  ) : (
                    <div className="text-center text-muted py-5">
                      <i className="fa fa-pie-chart fa-3x mb-3 d-block" style={{ opacity: 0.3 }}></i>
                      No hay citas en este período
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          {consultationStats && (
            <div className="row">
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="text-primary">{consultationStats.consultations.total}</h3>
                    <p className="text-muted mb-0">Consultas del Mes</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="text-success">{consultationStats.appointments.completed}</h3>
                    <p className="text-muted mb-0">Citas Completadas</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="text-warning">{consultationStats.appointments.pending}</h3>
                    <p className="text-muted mb-0">Citas Pendientes</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="text-info">{consultationStats.patients.active}</h3>
                    <p className="text-muted mb-0">Pacientes Activos</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tables Row */}
          <div className="row">
            {/* Recent Appointments */}
            <div className="col-12 col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Citas Recientes</h4>
                  <Link to="/appointments" className="patient-views float-end">Ver todas</Link>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0">
                      <thead>
                        <tr>
                          <th>Paciente</th>
                          <th>Doctor</th>
                          <th>Fecha</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentAppointments.length > 0 ? recentAppointments.map(appt => (
                          <tr key={appt.id}>
                            <td>{appt.patient_name || `${appt.patient?.first_name || ''} ${appt.patient?.last_name || ''}`}</td>
                            <td>{appt.doctor_name || `Dr. ${appt.doctor?.first_name || ''} ${appt.doctor?.last_name || ''}`}</td>
                            <td>{formatDate(appt.scheduled_date || appt.appointment_date)}</td>
                            <td>
                              <span className={`custom-badge ${
                                appt.status_code === 'COMPLETED' ? 'status-green' :
                                appt.status_code === 'CANCELLED' ? 'status-pink' :
                                appt.status_code === 'NO_SHOW' ? 'status-grey' :
                                'status-orange'
                              }`}>
                                {appt.status_name || '-'}
                              </span>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="4" className="text-center text-muted py-3">No hay citas recientes</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Patients */}
            <div className="col-12 col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Pacientes Recientes</h4>
                  <Link to="/patients" className="patient-views float-end">Ver todos</Link>
                </div>
                <div className="card-body p-0 table-dash">
                  <div className="table-responsive">
                    <table className="table mb-0 border-0">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Identificación</th>
                          <th>Teléfono</th>
                          <th>Registro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentPatients.length > 0 ? recentPatients.map(p => (
                          <tr key={p.id}>
                            <td>
                              <Link to={`/patients/${p.id}`}>{p.first_name} {p.last_name}</Link>
                            </td>
                            <td>{p.identification || '-'}</td>
                            <td>{p.phone || p.mobile || '-'}</td>
                            <td>{formatDate(p.registration_date || p.created_at)}</td>
                          </tr>
                        )) : (
                          <tr><td colSpan="4" className="text-center text-muted py-3">No hay pacientes registrados</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

    </>
  );
};

export default Admin_Dashboard;
